"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Star, Quote } from "lucide-react";
import Marquee from "react-fast-marquee";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { TextGradient } from "@/components/ui/text-gradient";
import { cn } from "@/lib/utils";

interface Testimonial {
  content: string;
  author: string;
  role: string;
  rating: number;
}

export function TestimonialsSection() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const testimonials: Testimonial[] = [
    {
      content: "Blue Legos Matter transformed my building experience. I never realized how underrepresented blue bricks were until they opened my eyes. Now my creations have more depth and character.",
      author: "Alex Johnson",
      role: "Amateur Builder",
      rating: 5,
    },
    {
      content: "As an educator, the Blue Education Program resources have been invaluable in teaching my students about color balance in design. Blue is now their favorite color to build with!",
      author: "Dr. Sarah Williams",
      role: "Elementary School Teacher",
      rating: 5,
    },
    {
      content: "The Blue Brick Rescue program helped our community center establish a complete Lego club. The quality of the restored blue pieces is outstanding.",
      author: "Michael Rodriguez",
      role: "Community Center Director",
      rating: 4,
    },
    {
      content: "I never thought about brick color bias until I attended a BLM workshop. Now I'm conscious about making sure blue pieces get equal representation in all my builds.",
      author: "Tanya Chen",
      role: "Professional Model Designer",
      rating: 5,
    },
    {
      content: "The research BLM is doing on color psychology in constructive play is groundbreaking. Their advocacy is changing how we think about blue bricks in development.",
      author: "Dr. James Martin",
      role: "Child Development Researcher",
      rating: 5,
    },
  ];

  const logoPartners = [
    "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg",
    "https://cdn.worldvectorlogo.com/logos/microsoft-5.svg",
    "https://cdn.worldvectorlogo.com/logos/amazon-icon-1.svg",
    "https://cdn.worldvectorlogo.com/logos/apple-13.svg",
    "https://cdn.worldvectorlogo.com/logos/facebook-3.svg",
    "https://cdn.worldvectorlogo.com/logos/twitter-6.svg",
    "https://cdn.worldvectorlogo.com/logos/instagram-2016-5.svg",
    "https://cdn.worldvectorlogo.com/logos/linkedin-icon-2.svg",
  ];

  return (
    <SectionContainer className="bg-gray-50 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pattern-bg"></div>

      <SectionHeading
        title={
          <>
            What People Say <TextGradient text="About Us" />
          </>
        }
        subtitle="Hear from builders, educators, and designers who have embraced the blue brick movement and seen the difference it makes."
      />

      <div
        ref={ref}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
      >
        {testimonials.slice(0, 3).map((testimonial, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className={cn(
              "bg-white rounded-xl p-6 shadow-sm border border-gray-100",
              "hover:shadow-md transition-all duration-300"
            )}
          >
            <Quote className="h-8 w-8 text-blue-100 mb-4" />
            <p className="text-muted-foreground mb-6">"{testimonial.content}"</p>
            
            <div className="flex mb-4">
              {Array(5).fill(0).map((_, starIndex) => (
                <Star 
                  key={starIndex} 
                  className={cn(
                    "h-4 w-4",
                    starIndex < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"
                  )} 
                />
              ))}
            </div>
            
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center mr-3">
                <span className="font-semibold text-blue-medium">
                  {testimonial.author.charAt(0)}
                </span>
              </div>
              <div>
                <h4 className="font-medium">{testimonial.author}</h4>
                <p className="text-sm text-muted-foreground">{testimonial.role}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Logo marquee */}
      <div className="mb-10">
        <p className="text-center text-sm font-medium text-muted-foreground mb-6">
          TRUSTED BY LEADING ORGANIZATIONS
        </p>
        <div className="bg-white py-8 px-2 rounded-lg border border-gray-100">
          <Marquee gradient={false} speed={30}>
            <div className="flex items-center gap-16">
              {logoPartners.map((logo, index) => (
                <div key={index} className="h-8 w-auto mx-8 opacity-70 hover:opacity-100 transition-opacity duration-300">
                  <img src={logo} alt="Partner logo" className="h-full w-auto" />
                </div>
              ))}
            </div>
          </Marquee>
        </div>
      </div>

      {/* Featured testimonial */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-gradient-to-r from-blue-700 to-blue-500 rounded-2xl p-6 md:p-10 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 opacity-10">
          <Quote className="h-40 w-40 transform rotate-180" />
        </div>
        
        <div className="max-w-3xl mx-auto relative z-10">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-center mb-6">
            "Blue Legos Matter has started a revolution in how we perceive and utilize colored bricks in design. Their advocacy has fundamentally changed our industry standards for the better."
          </h3>
          
          <div className="flex flex-col items-center">
            <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mb-3">
              <img 
                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=150&q=80" 
                alt="Emily Rodriguez" 
                className="h-14 w-14 rounded-full object-cover"
              />
            </div>
            <h4 className="font-semibold text-lg">Emily Rodriguez</h4>
            <p className="text-blue-100">Lead Designer, LEGO® Creative Studios</p>
          </div>
        </div>
      </motion.div>
    </SectionContainer>
  );
}